/**
 * Survie des abonnements, par cohorte d'abonnement.
 *
 * C'est la réponse à « les abonnés restent-ils abonnés ? ». La cohorte est
 * définie par le mois du PREMIER abonnement, pas de l'inscription : c'est ce qui
 * permet de comparer des abonnements de même âge, là où la conversion par
 * cohorte d'inscription mélange les âges.
 *
 * Trois horizons ORDINAUX — M+1 avant M+3 avant M+12 — d'où trois pas de la
 * rampe séquentielle. M+6 est dans le tableau : quatre séries de barres
 * groupées sur une douzaine de cohortes deviennent illisibles.
 *
 * ── Deux réserves ───────────────────────────────────────────────────────────
 * `subscriptions_stripe` n'a qu'UNE ligne par compte, écrasée : un compte qui
 * s'abonne, résilie, puis se réabonne n'expose que sa date d'origine et son
 * état courant. Les interruptions sont invisibles.
 *
 * Et un taux n'est publié qu'à partir de 20 abonnements ayant eu le temps
 * d'atteindre l'horizon — sinon un mois récent afficherait un taux calculé sur
 * trois personnes à côté d'un taux calculé sur mille.
 */

import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { MIN_GROUP_SIZE } from '@shared/types/stats';

import { formatInteger, formatMonthShort, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS, SEQUENTIAL_RAMP } from '../lib/theme';
import type { DataColumn, SubscriptionCohortRow } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';

const SERIES = [
  { key: 'rateM1', label: 'M+1', color: SEQUENTIAL_RAMP[1] as string },
  { key: 'rateM3', label: 'M+3', color: SEQUENTIAL_RAMP[3] as string },
  { key: 'rateM12', label: 'M+12', color: SEQUENTIAL_RAMP[5] as string },
];

const legend: readonly LegendEntry[] = SERIES.map((series) => ({
  label: series.label,
  color: series.color,
  shape: 'area',
}));

const dash = (value: number | null) => (value === null ? '—' : formatPercent(value));

const columns: ReadonlyArray<DataColumn<SubscriptionCohortRow>> = [
  { key: 'cohort', header: 'Abonnés en', render: (row) => formatMonthShort(row.cohort) },
  { key: 'n', header: 'Abonnements', numeric: true, render: (row) => formatInteger(row.subscriptions) },
  { key: 'e1', header: 'Élig. M+1', numeric: true, render: (row) => formatInteger(row.eligibleM1) },
  { key: 'm1', header: 'M+1', numeric: true, render: (row) => dash(row.rateM1) },
  { key: 'm3', header: 'M+3', numeric: true, render: (row) => dash(row.rateM3) },
  { key: 'm6', header: 'M+6', numeric: true, render: (row) => dash(row.rateM6) },
  { key: 'e12', header: 'Élig. M+12', numeric: true, render: (row) => formatInteger(row.eligibleM12) },
  { key: 'm12', header: 'M+12', numeric: true, render: (row) => dash(row.rateM12) },
];

export function SubscriptionCohortChart({ data }: { data: readonly SubscriptionCohortRow[] }) {
  return (
    <ChartCard
      title="Survie des abonnements par cohorte"
      subtitle={`Cohorte = mois du premier abonnement — un taux n'est publié qu'à partir de ${MIN_GROUP_SIZE} abonnements éligibles`}
      emptyMessage="Aucun abonnement démarré sur cette période."
      className="chart-grid__wide"
      rows={data}
      columns={columns}
      chartHeight={320}
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="88%">
        <BarChart data={data as SubscriptionCohortRow[]} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="cohort"
            tickFormatter={formatMonthShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={20}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip labelFormatter={formatMonthShort} valueFormatter={formatPercent} />}
          />
          {SERIES.map((series) => (
            <Bar
              key={series.key}
              isAnimationActive={false}
              dataKey={series.key}
              name={series.label}
              fill={series.color}
              radius={[3, 3, 0, 0]}
              maxBarSize={BAR_MAX_SIZE}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
